import { AxiosResponse } from 'axios';

import {
	LoadTheUserInterfaceStatesForTheUserQuery,
	UpdateTheUserInterfaceStatesForTheUserCommand,
	UserInterfaceStates,
	UserInterfaceStatesRunType,
} from 'src/models/stateProvider';

import { CommandBus } from 'src/util/command/CommandBus';
import { QueryStore } from 'src/util/query/QueryStore';
import { Callback } from 'src/util/query/models/Callback';
import { QueryStoreRequest } from 'src/util/query/models/QueryStoreRequest';

const RECENT_SEARCH_HISTORY_RETENTION_MONTHS = 6;
const APPROXIMATE_DAYS_PER_MONTH = 30;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_RECENT_SEARCH_HISTORY_ENTRIES_PER_MODULE = 50;
const RECENT_SEARCH_HISTORY_RETENTION_MS =
	RECENT_SEARCH_HISTORY_RETENTION_MONTHS * APPROXIMATE_DAYS_PER_MONTH * MILLISECONDS_PER_DAY;

type PersistedSearchHistoryEntry = {
	isFavorite?: boolean;
	timestamp?: number;
} & Record<string, unknown>;

class LoadUserInterfaceStatesCallback extends Callback<UserInterfaceStates> {
	private readonly resolve: (value: UserInterfaceStates) => void;
	private readonly reject: (reason: unknown) => void;

	constructor(resolve: (value: UserInterfaceStates) => void, reject: (reason: unknown) => void) {
		super();
		this.resolve = resolve;
		this.reject = reject;
	}

	successCallbackFunction = (response: UserInterfaceStates): void => {
		try {
			if (response && typeof response === 'object') {
				this.resolve(response);
			} else {
				this.resolve({});
			}
		} catch {
			this.resolve({});
		}
	};

	failureCallbackFunction = (error: unknown): void => {
		console.warn('Failed to load user interface states from backend:', error);
		this.reject(error);
	};
}

export class StateProviderService {
	private readonly queryStore: QueryStore;
	private readonly commandBus: CommandBus;

	constructor(queryStore: QueryStore, commandBus: CommandBus) {
		this.queryStore = queryStore;
		this.commandBus = commandBus;
	}

	async loadUserInterfaceStates(): Promise<UserInterfaceStates | null> {
		try {
			const query = new LoadTheUserInterfaceStatesForTheUserQuery();

			return new Promise<UserInterfaceStates | null>((resolve, reject) => {
				const request: QueryStoreRequest<UserInterfaceStates> = {
					callback: new LoadUserInterfaceStatesCallback(
						(states) => resolve(states),
						(error) => reject(error instanceof Error ? error : new Error(String(error)))
					),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: UserInterfaceStatesRunType,
				};

				void this.queryStore.query<UserInterfaceStates>(request);
			});
		} catch (error) {
			console.warn('Error loading user interface states:', error);
			return null;
		}
	}

	async saveUserInterfaceStates(states: UserInterfaceStates): Promise<boolean> {
		try {
			const jsonEncodedState = states.jsonEncodedState || '{}';

			if (jsonEncodedState.length > 10 * 1024 * 1024) {
				return false;
			}

			const command = new UpdateTheUserInterfaceStatesForTheUserCommand(jsonEncodedState);

			const response: AxiosResponse<unknown> | null = await this.commandBus.sendCommand(command);

			return response !== null && response.status >= 200 && response.status < 300;
		} catch (error) {
			console.warn('StateProvider: Failed to save user interface states to backend:', error);
			return false;
		}
	}

	mergeStatesWithFavorites(
		existingStates: UserInterfaceStates,
		newFavoritesData: Record<string, unknown>
	): UserInterfaceStates {
		const cleanedSearchHistory = this.cleanupSearchHistory(newFavoritesData);

		const cleanedBackendStates = this.cleanupBackendStates(existingStates);

		const merged = this.integrateSearchDataIntoJsonState(cleanedBackendStates, cleanedSearchHistory);

		return merged;
	}

	private integrateSearchDataIntoJsonState(
		backendStates: UserInterfaceStates,
		searchHistory: Record<string, unknown>
	): UserInterfaceStates {
		const result = { ...backendStates };

		if (result.jsonEncodedState) {
			try {
				const parsedState = JSON.parse(result.jsonEncodedState) as Record<string, unknown>;

				parsedState.searchHistory = JSON.stringify(searchHistory);
				result.jsonEncodedState = JSON.stringify(parsedState);
			} catch {
				result.searchHistory = JSON.stringify(searchHistory);
			}
		} else {
			const newJsonState = {
				searchHistory: JSON.stringify(searchHistory),
			};
			result.jsonEncodedState = JSON.stringify(newJsonState);
		}

		delete result.searchHistory;
		delete result.activeFavorites;

		return result;
	}

	extractFavoritesFromStates(states: UserInterfaceStates): {
		searchHistory: Record<string, unknown>;
	} {
		let searchHistory: Record<string, unknown> = {};

		try {
			if (states.searchHistory) {
				searchHistory = JSON.parse(states.searchHistory) as Record<string, unknown>;
			}
		} catch {
			/* empty */
		}

		if (states.jsonEncodedState) {
			try {
				const parsedState = JSON.parse(states.jsonEncodedState) as Record<string, unknown>;
				if (parsedState.searchHistory && typeof parsedState.searchHistory === 'string') {
					searchHistory = JSON.parse(parsedState.searchHistory) as Record<string, unknown>;
				}
			} catch {
				/* empty */
			}
		}

		return {
			searchHistory,
		};
	}

	private cleanupSearchHistory(searchHistory: Record<string, unknown>): Record<string, unknown> {
		const cleaned: Record<string, unknown> = {};
		const oldestRecentTimestamp = Date.now() - RECENT_SEARCH_HISTORY_RETENTION_MS;

		Object.keys(searchHistory).forEach((module) => {
			const entries = searchHistory[module];
			if (Array.isArray(entries)) {
				const searchHistoryEntries = entries as PersistedSearchHistoryEntry[];
				const favoriteEntries = searchHistoryEntries.filter((entry) => entry.isFavorite === true);
				const recentNonFavoriteEntries = searchHistoryEntries
					.filter(
						(entry) =>
							entry.isFavorite !== true && this.isRecentSearchHistoryEntry(entry, oldestRecentTimestamp)
					)
					.slice(0, MAX_RECENT_SEARCH_HISTORY_ENTRIES_PER_MODULE);

				const favoritesWithCorrectSortOrder = this.ensureCorrectSortOrder([
					...favoriteEntries,
					...recentNonFavoriteEntries,
				] as Record<string, unknown>[]);

				if (favoritesWithCorrectSortOrder.length > 0) {
					cleaned[module] = favoritesWithCorrectSortOrder;
				}
			}
		});

		return cleaned;
	}

	private isRecentSearchHistoryEntry(entry: PersistedSearchHistoryEntry, oldestRecentTimestamp: number): boolean {
		return typeof entry.timestamp === 'number' && entry.timestamp > oldestRecentTimestamp;
	}

	private ensureCorrectSortOrder(entries: Record<string, unknown>[]): Record<string, unknown>[] {
		const favorites = entries.filter((entry) => entry.isFavorite === true);
		const nonFavorites = entries.filter((entry) => entry.isFavorite !== true);

		const sortedFavorites = favorites.sort((a, b) => {
			if (typeof a.sortOrder === 'number' && typeof b.sortOrder === 'number') {
				return a.sortOrder - b.sortOrder;
			}
			if (typeof a.sortOrder === 'number') return -1;
			if (typeof b.sortOrder === 'number') return 1;

			const aLabel = typeof a.label === 'string' ? a.label : '';
			const bLabel = typeof b.label === 'string' ? b.label : '';
			return aLabel.localeCompare(bLabel);
		});

		const favoritesWithSortOrder = sortedFavorites.map((favorite, index) => ({
			...favorite,
			sortOrder: index,
		}));

		return [...favoritesWithSortOrder, ...nonFavorites];
	}

	private cleanupBackendStates(backendStates: UserInterfaceStates): UserInterfaceStates {
		const cleaned: UserInterfaceStates = {};

		if (backendStates.jsonEncodedState) {
			try {
				const parsed = JSON.parse(backendStates.jsonEncodedState) as Record<string, unknown>;
				const cleanedParsed: Record<string, unknown> = {};

				Object.keys(parsed).forEach((key) => {
					const value = parsed[key];
					const valueStr = typeof value === 'string' ? value : JSON.stringify(value);

					if (valueStr.length < 1024 * 1024) {
						cleanedParsed[key] = value;
					} else {
						console.warn(`StateProvider: Entferne großen Backend-State: ${key} (${valueStr.length} bytes)`);
					}
				});

				cleaned.jsonEncodedState = JSON.stringify(cleanedParsed);
			} catch {
				cleaned.jsonEncodedState = '{}';
			}
		}

		Object.keys(backendStates).forEach((key) => {
			if (key !== 'jsonEncodedState') {
				cleaned[key] = backendStates[key];
			}
		});

		return cleaned;
	}
}
